import { createSlice, createSelector, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../constants/config';

// Helper function to normalize an array of entities by ID
const normalizeById = (entities, idKey = 'uuid') => {
  const byId = {};
  const allIds = [];

  entities.forEach(entity => {
    const id = entity[idKey];
    if (id) {
      byId[id] = entity;
      allIds.push(id);
    }
  });

  return { byId, allIds };
};

// Helper function to group entities by a parent ID
const groupByParentId = (entities, parentIdKey, idKey = 'uuid') => {
  const byParent = {};

  entities.forEach(entity => {
    const parentId = entity[parentIdKey];
    const id = entity[idKey];

    if (parentId && id) {
      if (!byParent[parentId]) {
        byParent[parentId] = [];
      }

      if (!byParent[parentId].includes(id)) {
        byParent[parentId].push(id);
      }
    }
  });

  return byParent;
};

// Async thunks for API calls
// Literary Group thunks
export const fetchLitGroups = createAsyncThunk(
  'newBookCreator/fetchLitGroups',
  async (_, { rejectWithValue }) => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${config.GET_API_DIRECT_LIT_GROUPS}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data.litGroups;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchLitGroup = createAsyncThunk(
  'newBookCreator/fetchLitGroup',
  async (litGroupId, { rejectWithValue }) => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('No authentication token');
      }

      const endpoint = config.GET_API_DIRECT_LIT_GROUP_PROFILE.replace(':litGroupUuid', litGroupId);
      console.log(`[FETCH_LIT_GROUP] Fetching literary group from endpoint: ${endpoint}`);

      const response = await fetch(`${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[FETCH_LIT_GROUP] Error response: ${errorText}`);
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`[FETCH_LIT_GROUP] Received data:`, data);
      console.log(`[FETCH_LIT_GROUP] Profile data:`, data.profile);

      // Check if the profile data has the expected fields
      if (data.profile) {
        console.log(`[FETCH_LIT_GROUP] Profile fields:`, Object.keys(data.profile));
        console.log(`[FETCH_LIT_GROUP] Profile name:`, data.profile.name);
        console.log(`[FETCH_LIT_GROUP] Profile description:`, data.profile.description);
        console.log(`[FETCH_LIT_GROUP] Profile lgType:`, data.profile.lgType);
      }

      return data.profile;
    } catch (error) {
      console.error(`[FETCH_LIT_GROUP] Error:`, error);
      return rejectWithValue(error.message);
    }
  }
);

// Book thunks
export const fetchBooks = createAsyncThunk(
  'newBookCreator/fetchBooks',
  async (litGroupId, { rejectWithValue }) => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('No authentication token');
      }

      const endpoint = config.GET_API_DIRECT_BOOKS_BY_LIT_GROUP.replace(':litGroupUuid', litGroupId);
      const response = await fetch(`${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return { books: data.books, litGroupId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchBook = createAsyncThunk(
  'newBookCreator/fetchBook',
  async (bookId, { rejectWithValue }) => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('No authentication token');
      }

      const endpoint = config.GET_API_BOOK_PROFILE.replace(':bookUuid', bookId);
      const response = await fetch(`${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data.profile;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Character thunks
export const fetchCharacters = createAsyncThunk(
  'newBookCreator/fetchCharacters',
  async (bookId, { rejectWithValue }) => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('No authentication token');
      }

      const endpoint = config.GET_API_DIRECT_CHARACTERS_BY_BOOK.replace(':bookUuid', bookId);
      const response = await fetch(`${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return { characters: data.characters, bookId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCharacter = createAsyncThunk(
  'newBookCreator/fetchCharacter',
  async ({ characterId, bookId }, { rejectWithValue, getState }) => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('No authentication token');
      }

      // If bookId is not provided, get it from the state
      let finalBookId = bookId;
      if (!finalBookId) {
        const state = getState();
        finalBookId = state.user.newBookCreatorNormalized.ui.selections.book;
      }

      if (!finalBookId) {
        throw new Error('No book selected and no bookId provided');
      }

      const endpoint = config.GET_API_DIRECT_CHARACTER_BY_BOOK
        .replace(':bookUuid', finalBookId)
        .replace(':characterUuid', characterId);

      // Add cache busting to ensure fresh data
      const cacheBust = `?cacheBust=${Date.now()}`;
      const finalEndpoint = `${endpoint}${cacheBust}`;

      const response = await fetch(finalEndpoint, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return { character: data, bookId: finalBookId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateCharacter = createAsyncThunk(
  'newBookCreator/updateCharacter',
  async ({ characterId, bookId, data }, { rejectWithValue, getState }) => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('No authentication token');
      }

      // If bookId is not provided, get it from the state
      let finalBookId = bookId;
      if (!finalBookId) {
        const state = getState();
        finalBookId = state.user.newBookCreatorNormalized.ui.selections.book;
      }

      if (!finalBookId) {
        throw new Error('No book selected and no bookId provided');
      }

      const endpoint = config.PUT_API_DIRECT_CHARACTER_BY_BOOK
        .replace(':bookUuid', finalBookId)
        .replace(':characterUuid', characterId);

      // Check if the avatarUrl is a blob URL and fix it if needed
      let finalData = { ...data };
      if (finalData.avatarUrl && finalData.avatarUrl.startsWith('blob:')) {
        console.log('WARNING: Attempting to save blob URL to database. Replacing with default avatar URL.');
        finalData.avatarUrl = '/narrator_1-1-480x490.png'; // Use a default avatar URL
      }

      const response = await fetch(`${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(finalData)
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const updatedData = await response.json();
      return { character: updatedData, bookId: finalBookId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createCharacter = createAsyncThunk(
  'newBookCreator/createCharacter',
  async ({ bookId, data }, { rejectWithValue, getState }) => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('No authentication token');
      }

      // If bookId is not provided, get it from the state
      let finalBookId = bookId;
      if (!finalBookId) {
        const state = getState();
        finalBookId = state.user.newBookCreatorNormalized.ui.selections.book;
      }

      if (!finalBookId) {
        throw new Error('No book selected and no bookId provided');
      }

      const endpoint = config.POST_API_ADD_CHAR_TO_BOOK; // Using the correctly named constant

      // Check if the avatarUrl is a blob URL and fix it if needed
      let finalData = { ...data, bookId: finalBookId };
      if (finalData.avatarUrl && finalData.avatarUrl.startsWith('blob:')) {
        console.log('WARNING: Attempting to save blob URL to database. Replacing with default avatar URL.');
        finalData.avatarUrl = '/narrator_1-1-480x490.png'; // Use a default avatar URL
      }

      const response = await fetch(`${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(finalData)
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const createdData = await response.json();
      return { character: createdData, bookId: finalBookId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Variation thunks
export const fetchVariations = createAsyncThunk(
  'newBookCreator/fetchVariations',
  async (characterId, { rejectWithValue }) => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('No authentication token');
      }

      const endpoint = config.GET_API_DIRECT_VARIATIONS_BY_CHARACTER.replace(':characterUuid', characterId);
      const response = await fetch(`${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return { variations: data.variations, characterId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Initial state with normalized structure
const initialState = {
  entities: {
    litGroups: { byId: {}, allIds: [] },
    books: { byId: {}, allIds: [], byLitGroup: {} },
    characters: { byId: {}, allIds: [], byBook: {} },
    variations: { byId: {}, allIds: [], byCharacter: {} }
  },
  ui: {
    selections: {
      litGroup: null,
      book: null,
      character: null,
      variation: null
    },
    persistSelections: true,
    loadingStates: {
      litGroups: 'idle',
      books: 'idle',
      characters: 'idle',
      variations: 'idle'
    },
    errors: {
      litGroups: null,
      books: null,
      characters: null,
      variations: null
    },
    forms: {
      character: {
        isDirty: false,
        isSubmitting: false,
        errors: null
      }
    },
    lastUpdated: {
      litGroups: null,
      books: null,
      characters: null,
      variations: null
    }
  },
  cache: {
    signedUrls: {}
  }
};

// Create the slice
const newBookCreatorSlice = createSlice({
  name: 'newBookCreator',
  initialState,
  reducers: {
    // Selection actions
    setSelection: (state, action) => {
      const { level, id } = action.payload;
      state.ui.selections[level] = id;
    },
    clearSelection: (state, action) => {
      const level = action.payload;

      if (level) {
        state.ui.selections[level] = null;

        // Clear dependent selections
        if (level === 'litGroup') {
          state.ui.selections.book = null;
          state.ui.selections.character = null;
          state.ui.selections.variation = null;
        } else if (level === 'book') {
          state.ui.selections.character = null;
          state.ui.selections.variation = null;
        } else if (level === 'character') {
          state.ui.selections.variation = null;
        }
      } else {
        // Clear all selections
        state.ui.selections = {
          litGroup: null,
          book: null,
          character: null,
          variation: null
        };
      }
    },
    setPersistSelections: (state, action) => {
      state.ui.persistSelections = action.payload;
    },

    // Form state actions
    setCharacterFormDirty: (state, action) => {
      state.ui.forms.character.isDirty = action.payload;
    },
    setCharacterFormSubmitting: (state, action) => {
      state.ui.forms.character.isSubmitting = action.payload;
    },
    setCharacterFormErrors: (state, action) => {
      state.ui.forms.character.errors = action.payload;
    },

    // Cache actions
    addSignedUrl: (state, action) => {
      const { originalUrl, signedUrl } = action.payload;
      state.cache.signedUrls[originalUrl] = {
        url: signedUrl,
        timestamp: Date.now()
      };
    },
    clearCache: (state) => {
      state.cache = {
        signedUrls: {}
      };
    }
  },
  extraReducers: (builder) => {
    // Literary Group reducers
    builder
      .addCase(fetchLitGroups.pending, (state) => {
        state.ui.loadingStates.litGroups = 'loading';
      })
      .addCase(fetchLitGroups.fulfilled, (state, action) => {
        state.ui.loadingStates.litGroups = 'succeeded';

        // Normalize the data
        const { byId, allIds } = normalizeById(action.payload);

        // Log the current and new literary groups
        console.log('[REDUX] Current literary groups:', Object.keys(state.entities.litGroups.byId).length);
        console.log('[REDUX] New literary groups:', Object.keys(byId).length);

        // Replace the existing literary groups instead of merging
        state.entities.litGroups.byId = byId;
        state.entities.litGroups.allIds = allIds;

        state.ui.errors.litGroups = null;
        state.ui.lastUpdated.litGroups = Date.now();
      })
      .addCase(fetchLitGroups.rejected, (state, action) => {
        state.ui.loadingStates.litGroups = 'failed';
        state.ui.errors.litGroups = action.payload || 'Failed to fetch literary groups';
      })

      .addCase(fetchLitGroup.pending, (state) => {
        state.ui.loadingStates.litGroups = 'loading';
      })
      .addCase(fetchLitGroup.fulfilled, (state, action) => {
        state.ui.loadingStates.litGroups = 'succeeded';

        // Add or update the lit group in the store
        const litGroup = action.payload;
        if (litGroup && litGroup.uuid) {
          console.log(`[REDUX] Updating lit group in store with UUID: ${litGroup.uuid}`);

          // Update the lit group in the store
          state.entities.litGroups.byId[litGroup.uuid] = litGroup;

          if (!state.entities.litGroups.allIds.includes(litGroup.uuid)) {
            state.entities.litGroups.allIds.push(litGroup.uuid);
          }

          // Update the selected lit group ID if it's different
          const selectedLitGroupId = state.ui.selections.litGroup;
          if (selectedLitGroupId && selectedLitGroupId !== litGroup.uuid) {
            console.log(`[REDUX] Updating selected lit group ID from ${selectedLitGroupId} to ${litGroup.uuid}`);
            state.ui.selections.litGroup = litGroup.uuid;
          }
        }

        state.ui.errors.litGroups = null;
      })
      .addCase(fetchLitGroup.rejected, (state, action) => {
        state.ui.loadingStates.litGroups = 'failed';
        state.ui.errors.litGroups = action.payload || 'Failed to fetch literary group';
      })

      // Book reducers
      .addCase(fetchBooks.pending, (state) => {
        state.ui.loadingStates.books = 'loading';
      })
      .addCase(fetchBooks.fulfilled, (state, action) => {
        state.ui.loadingStates.books = 'succeeded';

        const { books, litGroupId } = action.payload;

        // Normalize the data
        const { byId, allIds } = normalizeById(books);

        // Update the byId and allIds
        state.entities.books.byId = {
          ...state.entities.books.byId,
          ...byId
        };

        // Merge allIds without duplicates
        const newAllIds = [...state.entities.books.allIds];
        allIds.forEach(id => {
          if (!newAllIds.includes(id)) {
            newAllIds.push(id);
          }
        });
        state.entities.books.allIds = newAllIds;

        // Update the byLitGroup index
        state.entities.books.byLitGroup[litGroupId] = allIds;

        state.ui.errors.books = null;
        state.ui.lastUpdated.books = Date.now();
      })
      .addCase(fetchBooks.rejected, (state, action) => {
        state.ui.loadingStates.books = 'failed';
        state.ui.errors.books = action.payload || 'Failed to fetch books';
      })

      .addCase(fetchBook.pending, (state) => {
        state.ui.loadingStates.books = 'loading';
      })
      .addCase(fetchBook.fulfilled, (state, action) => {
        state.ui.loadingStates.books = 'succeeded';

        // Add or update the book in the store
        const book = action.payload;
        if (book && book.uuid) {
          state.entities.books.byId[book.uuid] = book;

          if (!state.entities.books.allIds.includes(book.uuid)) {
            state.entities.books.allIds.push(book.uuid);
          }

          // Update the byLitGroup index if litGroupId is available
          if (book.litGroupId) {
            if (!state.entities.books.byLitGroup[book.litGroupId]) {
              state.entities.books.byLitGroup[book.litGroupId] = [];
            }

            if (!state.entities.books.byLitGroup[book.litGroupId].includes(book.uuid)) {
              state.entities.books.byLitGroup[book.litGroupId].push(book.uuid);
            }
          }
        }

        state.ui.errors.books = null;
      })
      .addCase(fetchBook.rejected, (state, action) => {
        state.ui.loadingStates.books = 'failed';
        state.ui.errors.books = action.payload || 'Failed to fetch book';
      })

      // Character reducers
      .addCase(fetchCharacters.pending, (state) => {
        state.ui.loadingStates.characters = 'loading';
      })
      .addCase(fetchCharacters.fulfilled, (state, action) => {
        state.ui.loadingStates.characters = 'succeeded';

        const { characters, bookId } = action.payload;

        // Normalize the data
        const { byId, allIds } = normalizeById(characters);

        // Update the byId and allIds
        state.entities.characters.byId = {
          ...state.entities.characters.byId,
          ...byId
        };

        // Merge allIds without duplicates
        const newAllIds = [...state.entities.characters.allIds];
        allIds.forEach(id => {
          if (!newAllIds.includes(id)) {
            newAllIds.push(id);
          }
        });
        state.entities.characters.allIds = newAllIds;

        // Update the byBook index
        state.entities.characters.byBook[bookId] = allIds;

        state.ui.errors.characters = null;
        state.ui.lastUpdated.characters = Date.now();
      })
      .addCase(fetchCharacters.rejected, (state, action) => {
        state.ui.loadingStates.characters = 'failed';
        state.ui.errors.characters = action.payload || 'Failed to fetch characters';
      })

      .addCase(fetchCharacter.pending, (state) => {
        state.ui.loadingStates.characters = 'loading';
      })
      .addCase(fetchCharacter.fulfilled, (state, action) => {
        state.ui.loadingStates.characters = 'succeeded';

        // Add or update the character in the store
        const { character, bookId } = action.payload;
        if (character && character.uuid) {
          state.entities.characters.byId[character.uuid] = character;

          if (!state.entities.characters.allIds.includes(character.uuid)) {
            state.entities.characters.allIds.push(character.uuid);
          }

          // Update the byBook index
          if (!state.entities.characters.byBook[bookId]) {
            state.entities.characters.byBook[bookId] = [];
          }

          if (!state.entities.characters.byBook[bookId].includes(character.uuid)) {
            state.entities.characters.byBook[bookId].push(character.uuid);
          }
        }

        state.ui.errors.characters = null;
      })
      .addCase(fetchCharacter.rejected, (state, action) => {
        state.ui.loadingStates.characters = 'failed';
        state.ui.errors.characters = action.payload || 'Failed to fetch character';
      })

      .addCase(updateCharacter.pending, (state) => {
        state.ui.forms.character.isSubmitting = true;
      })
      .addCase(updateCharacter.fulfilled, (state, action) => {
        state.ui.forms.character.isSubmitting = false;
        state.ui.forms.character.isDirty = false;
        state.ui.forms.character.errors = null;

        // Add or update the character in the store
        const { character, bookId } = action.payload;
        if (character && character.uuid) {
          state.entities.characters.byId[character.uuid] = character;

          if (!state.entities.characters.allIds.includes(character.uuid)) {
            state.entities.characters.allIds.push(character.uuid);
          }

          // Update the byBook index
          if (!state.entities.characters.byBook[bookId]) {
            state.entities.characters.byBook[bookId] = [];
          }

          if (!state.entities.characters.byBook[bookId].includes(character.uuid)) {
            state.entities.characters.byBook[bookId].push(character.uuid);
          }
        }

        state.ui.lastUpdated.characters = Date.now();
      })
      .addCase(updateCharacter.rejected, (state, action) => {
        state.ui.forms.character.isSubmitting = false;
        state.ui.forms.character.errors = action.payload || 'Failed to update character';
      })

      .addCase(createCharacter.pending, (state) => {
        state.ui.forms.character.isSubmitting = true;
      })
      .addCase(createCharacter.fulfilled, (state, action) => {
        state.ui.forms.character.isSubmitting = false;
        state.ui.forms.character.isDirty = false;
        state.ui.forms.character.errors = null;

        // Add the new character to the store
        const { character, bookId } = action.payload;
        if (character && character.uuid) {
          state.entities.characters.byId[character.uuid] = character;
          state.entities.characters.allIds.push(character.uuid);

          // Update the byBook index
          if (!state.entities.characters.byBook[bookId]) {
            state.entities.characters.byBook[bookId] = [];
          }

          state.entities.characters.byBook[bookId].push(character.uuid);

          // Set the selection to the new character
          state.ui.selections.character = character.uuid;
        }

        state.ui.lastUpdated.characters = Date.now();
      })
      .addCase(createCharacter.rejected, (state, action) => {
        state.ui.forms.character.isSubmitting = false;
        state.ui.forms.character.errors = action.payload || 'Failed to create character';
      })

      // Variation reducers
      .addCase(fetchVariations.pending, (state, action) => {
        state.ui.loadingStates.variations = 'loading';
      })
      .addCase(fetchVariations.fulfilled, (state, action) => {
        state.ui.loadingStates.variations = 'succeeded';

        const { variations, characterId } = action.payload;

        // Normalize the data
        const { byId, allIds } = normalizeById(variations);

        // Update the byId and allIds
        state.entities.variations.byId = {
          ...state.entities.variations.byId,
          ...byId
        };

        // Merge allIds without duplicates
        const newAllIds = [...state.entities.variations.allIds];
        allIds.forEach(id => {
          if (!newAllIds.includes(id)) {
            newAllIds.push(id);
          }
        });
        state.entities.variations.allIds = newAllIds;

        // Update the byCharacter index
        state.entities.variations.byCharacter[characterId] = allIds;

        state.ui.errors.variations = null;
        state.ui.lastUpdated.variations = Date.now();
      })
      .addCase(fetchVariations.rejected, (state, action) => {
        state.ui.loadingStates.variations = 'failed';
        state.ui.errors.variations = action.payload || 'Failed to fetch variations';
      });
  }
});

// Export actions
export const {
  setSelection,
  clearSelection,
  setPersistSelections,
  setCharacterFormDirty,
  setCharacterFormSubmitting,
  setCharacterFormErrors,
  addSignedUrl,
  clearCache
} = newBookCreatorSlice.actions;

// Selectors
// Basic selectors
export const selectEntities = state => state.user.newBookCreatorNormalized.entities;
export const selectUI = state => state.user.newBookCreatorNormalized.ui;
export const selectCache = state => state.user.newBookCreatorNormalized.cache;

// Selection selectors
export const selectSelections = state => state.user.newBookCreatorNormalized.ui.selections;
export const selectSelectionId = (state, level) => state.user.newBookCreatorNormalized.ui.selections[level];
export const selectPersistSelections = state => state.user.newBookCreatorNormalized.ui.persistSelections;

// Loading state selectors
export const selectLoadingState = (state, entityType) => state.user.newBookCreatorNormalized.ui.loadingStates[entityType];
export const selectError = (state, entityType) => state.user.newBookCreatorNormalized.ui.errors[entityType];

// Form state selectors
export const selectCharacterFormState = state => state.user.newBookCreatorNormalized.ui.forms.character;

// Entity selectors
export const selectLitGroups = state => {
  const { byId, allIds } = state.user.newBookCreatorNormalized.entities.litGroups;
  return allIds.map(id => byId[id]);
};

export const selectBooks = state => {
  const { byId, allIds } = state.user.newBookCreatorNormalized.entities.books;
  return allIds.map(id => byId[id]);
};

export const selectBooksByLitGroup = (state, litGroupId) => {
  const { byId, byLitGroup } = state.user.newBookCreatorNormalized.entities.books;
  const bookIds = byLitGroup[litGroupId] || [];
  return bookIds.map(id => byId[id]);
};

export const selectCharacters = state => {
  const { byId, allIds } = state.user.newBookCreatorNormalized.entities.characters;
  return allIds.map(id => byId[id]);
};

export const selectCharactersByBook = (state, bookId) => {
  const { byId, byBook } = state.user.newBookCreatorNormalized.entities.characters;
  const characterIds = byBook[bookId] || [];
  return characterIds.map(id => byId[id]);
};

export const selectVariations = state => {
  const { byId, allIds } = state.user.newBookCreatorNormalized.entities.variations;
  return allIds.map(id => byId[id]);
};

export const selectVariationsByCharacter = (state, characterId) => {
  const { byId, byCharacter } = state.user.newBookCreatorNormalized.entities.variations;
  const variationIds = byCharacter[characterId] || [];
  return variationIds.map(id => byId[id]);
};

// Selected entity selectors
export const selectSelectedLitGroup = state => {
  const litGroupId = state.user.newBookCreatorNormalized.ui.selections.litGroup;
  return litGroupId ? state.user.newBookCreatorNormalized.entities.litGroups.byId[litGroupId] : null;
};

export const selectSelectedBook = state => {
  const bookId = state.user.newBookCreatorNormalized.ui.selections.book;
  return bookId ? state.user.newBookCreatorNormalized.entities.books.byId[bookId] : null;
};

export const selectSelectedCharacter = state => {
  const characterId = state.user.newBookCreatorNormalized.ui.selections.character;
  return characterId ? state.user.newBookCreatorNormalized.entities.characters.byId[characterId] : null;
};

export const selectSelectedVariation = state => {
  const variationId = state.user.newBookCreatorNormalized.ui.selections.variation;
  return variationId ? state.user.newBookCreatorNormalized.entities.variations.byId[variationId] : null;
};

// Last updated selectors
export const selectLastUpdated = (state, entityType) => state.user.newBookCreatorNormalized.ui.lastUpdated[entityType];

// Cache selectors
export const selectSignedUrl = (state, originalUrl) => {
  const cacheEntry = state.user.newBookCreatorNormalized.cache.signedUrls[originalUrl];
  if (!cacheEntry) return null;

  // Check if the URL is still valid (less than 1 hour old)
  const now = Date.now();
  const oneHour = 60 * 60 * 1000;
  if (now - cacheEntry.timestamp > oneHour) return null;

  return cacheEntry.url;
};

export default newBookCreatorSlice.reducer;
